
import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { createApiRequest } from "@/utils/apiUtils";
import { API_URL } from "@/config/api";

interface PlaceResult {
  place_id: string;
  description: string;
  geocode?: Array<{
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
  }>;
}

interface AutocompleteAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  readOnly?: boolean;
  style?: React.CSSProperties;
}

export const AutocompleteAddressInput = ({
  value,
  onChange,
  placeholder,
  className,
  readOnly = false,
  style
}: AutocompleteAddressInputProps) => {
  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  const searchPlaces = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const url = `${API_URL}places/search/?q=${encodeURIComponent(query)}`;
      console.log("Searching places with URL:", url);
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        console.log("Places API search response:", data);
        console.log("Testing geometry data availability:");
        data.forEach((place: PlaceResult, index: number) => {
          console.log(`Place ${index + 1}:`, {
            description: place.description,
            place_id: place.place_id,
            hasGeometry: !!(place.geocode && place.geocode[0]?.geometry),
            geometry: place.geocode?.[0]?.geometry
          });
        });
        
        if (Array.isArray(data) && data.length > 0) {
          setSuggestions(data);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    } catch (error) {
      console.error("Error searching places:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion: PlaceResult) => {
    console.log("Suggestion clicked:", suggestion);
    onChange(suggestion.description);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Only hide suggestions if not clicking within the container
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
      }
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        className={className}
        readOnly={readOnly}
        style={style}
      />
      
      {showSuggestions && suggestions.length > 0 && !readOnly && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing
                handleSuggestionClick(suggestion);
              }}
            >
              <div className="text-sm text-gray-800">{suggestion.description}</div>
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
        </div>
      )}
    </div>
  );
};
